<script lang="ts">
export interface DateTimePickerProps
	extends DeprecatedPlacementProps,
		PlacementProps,
		FormFieldProps {
	modelValue?: DateTime | [DateTime, DateTime] | [];
	min?: DateTime;
	max?: DateTime;
	required?: boolean;
	disableTime?: boolean;
	range?: boolean;
	enableSeconds?: boolean;
	format?: Intl.DateTimeFormatOptions;
	formatter?: (value: DateTime | [DateTime, DateTime] | []) => string | null | undefined;
}
</script>

<script setup lang="ts">
import "@vuepic/vue-datepicker/dist/main.css";
import { DateTime } from "luxon";
import {
	type Alignment,
	type DeprecatedPlacementProps,
	type PlacementProps,
	type Side,
} from "../../lib/placement";
import type { FormFieldProps } from "../form/FormField.vue";
import "./date-time-picker.css";

import {
	mdiCalendar,
	mdiChevronDown,
	mdiChevronLeft,
	mdiChevronRight,
	mdiChevronUp,
	mdiClock,
} from "@mdi/js";
import { VueDatePicker } from "@vuepic/vue-datepicker";
import { computed, shallowRef, watch } from "vue";
import { useScreenSize } from "../../composables/screen-size";
import { useLocale } from "../../locale";
import Button from "../button/Button.vue";
import FormField from "../form/FormField.vue";
import Icon from "../icon/Icon.vue";
import Input from "../input/Input.vue";
import { InputGroup } from "../inputgroup";
import { Popover, PopoverClose } from "../popover";

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<DateTimePickerProps>(), {
	format: () => DateTime.DATETIME_FULL,
});
const emit = defineEmits<{
	(evt: "update:modelValue", val: DateTime | DateTime[]): void;
}>();

const { t } = useLocale();

const lazyValue = shallowRef(props.modelValue);
watch(
	() => props.modelValue,
	() => {
		lazyValue.value = props.modelValue;
	},
	{ immediate: true }
);

const zone = computed(() =>
	Array.isArray(lazyValue.value) ? lazyValue.value.at(0)?.zone : lazyValue.value?.zone
);

const locale = computed(() =>
	Array.isArray(lazyValue.value) ? lazyValue.value.at(0)?.locale : lazyValue.value?.locale
);

function toJSDate(date: DateTime) {
	return date.toJSDate();
}

function fromJSDate(date: Date) {
	return DateTime.fromISO(date.toISOString(), {
		zone: zone.value,
		locale: locale.value || undefined,
	});
}

const vModel = computed({
	get() {
		if (!lazyValue.value) return undefined;

		if (Array.isArray(lazyValue.value)) {
			return lazyValue.value.map(toJSDate);
		}

		return toJSDate(lazyValue.value);
	},
	set(date) {
		if (date) {
			if (Array.isArray(date)) {
				lazyValue.value = [fromJSDate(date[0]), fromJSDate(date[1])];
			} else {
				lazyValue.value = fromJSDate(date);
			}

			if (lazyValue.value) {
				emit("update:modelValue", lazyValue.value);
			}
		}
	},
});

const minDate = computed(() => {
	if (!props.min) return undefined;

	return props.min.toJSDate();
});

const maxDate = computed(() => {
	if (!props.max) return undefined;

	return props.max.toJSDate();
});

function dateTimeToLocaleString(datetime: DateTime) {
	return datetime.toLocaleString(props.format);
}

const formattedTime = computed(() => {
	if (!lazyValue.value) return "";

	if (props.formatter != null) {
		const formatted = props.formatter(lazyValue.value);
		if (formatted != null) {
			return formatted;
		}
	}

	if (Array.isArray(lazyValue.value)) {
		if (lazyValue.value.length === 2) {
			const start = lazyValue.value[0];
			const end = lazyValue.value[1];

			if (start.day === end.day) {
				return `${dateTimeToLocaleString(start)} - ${end.toLocaleString(DateTime.TIME_SIMPLE)}`;
			} else {
				return `${dateTimeToLocaleString(start)} - ${dateTimeToLocaleString(end)}`;
			}
			// Should never happen
		} else {
			return lazyValue.value.map(dateTimeToLocaleString).join(" - ");
		}
	}

	return dateTimeToLocaleString(lazyValue.value);
});

const { mdOrLower } = useScreenSize();
const side = computed<Side>(() => (mdOrLower.value ? "bottom" : "right"));
const sideOffset = computed(() => (mdOrLower.value ? 0 : 8));
const align = computed<Alignment>(() => (mdOrLower.value ? "center" : "start"));
const alignOffset = computed(() => (mdOrLower.value ? 0 : -100));
</script>

<template>
	<Popover :align="align" :alignOffset="alignOffset" :side="side" :offset="sideOffset">
		<template #trigger>
			<FormField as="button" :label="props.label" tabindex="-1" v-bind="$attrs">
				<InputGroup class="w-full">
					<Icon :d="mdiCalendar" />
					<Input :modelValue="formattedTime" readonly v-bind="$attrs" />
				</InputGroup>
			</FormField>
		</template>

		<div class="p-2 pb-0">
			<VueDatePicker
				v-model="vModel"
				inline
				:weekStart="0"
				:config="{ closeOnAutoApply: false, keepActionRow: true }"
				is24
				autoApply
				preventMinMaxNavigation
				:highlight="{ weekdays: [0, 6] }"
				:maxDate="maxDate"
				:minDate="minDate"
				:enableTimePicker="!props.disableTime"
				:timezone="zone?.name"
				:range="props.range"
				:enableSeconds="props.enableSeconds"
			>
				<template #action-buttons>
					<PopoverClose>
						<Button variant="ghost" color="primary">
							{{ t("close") }}
						</Button>
					</PopoverClose>
				</template>

				<template #action-preview></template>

				<template #am-pm-button="{ value, toggle }">
					<Button size="lg" variant="ghost" @click="toggle">
						{{ value }}
					</Button>
				</template>

				<template #clock-icon>
					<Icon size="lg" :d="mdiClock" />
				</template>
				<template #calendar-icon>
					<Icon size="lg" :d="mdiCalendar" />
				</template>
				<template #arrow-right>
					<Icon size="lg" :d="mdiChevronRight" />
				</template>
				<template #arrow-left>
					<Icon size="lg" :d="mdiChevronLeft" />
				</template>
				<template #arrow-up>
					<Icon size="lg" :d="mdiChevronUp" />
				</template>
				<template #arrow-down>
					<Icon size="lg" :d="mdiChevronDown" />
				</template>
			</VueDatePicker>
		</div>
	</Popover>
</template>
